import React, { useEffect, useState } from "react"

import useTracking from "hooks/useTracking"

import RecordedChatMessage from "components/webinar/components/chat/recorded-chat-message"
import VideoWithChatWrapper from "components/webinar/components/video-with-chat"
import ContentfulVideo from "components/page-components/video"

function csvToObjects(csvString) {
  const rows = csvString.split("\n")
  const data = rows.slice(1)

  const headers = [
    "sender",
    "senderName",
    "senderUserId",
    "senderRole",
    "time",
    "type",
    "message",
    "id",
  ]

  const objects = data.map((row) => {
    const values = row.split(",")
    const message = values
      .slice(6, -1)
      .join(",")
      .replace(/^"(.*)"$/, "$1")
    const updatedValues = [...values.slice(0, 6), message, values.at(-1)]
    const obj = updatedValues.reduce((acc, value, index) => {
      const key = headers[index]
      acc[key] = value
      return acc
    }, {})
    return obj
  })

  const startTime = new Date(objects[0].time)
  const elapsedSecondsMap = new Map()
  objects.forEach((obj) => {
    const endTime = new Date(obj.time)
    const elapsedSeconds = (endTime - startTime) / 1000
    const existingValue = elapsedSecondsMap.get(elapsedSeconds)
    if (existingValue) {
      if (Array.isArray(existingValue)) {
        existingValue.push(obj)
      } else {
        elapsedSecondsMap.set(elapsedSeconds, [existingValue, obj])
      }
    } else {
      elapsedSecondsMap.set(elapsedSeconds, obj)
    }
  })

  return elapsedSecondsMap
}

function extractElementsUpToKey(map, key, keys) {
  const index = keys.indexOf(key)
  if (index === -1) {
    return []
  }

  const elements = keys.slice(0, index + 1).flatMap((k) => {
    const value = map.get(k)
    return Array.isArray(value) ? value : [value]
  })
  return elements
}

function findLowerKeysAndExtract(map, value) {
  const lowerKeys = [...map.keys()].filter((key) => key < value)
  if (lowerKeys.length === 0) {
    return []
  }
  return extractElementsUpToKey(map, Math.max(...lowerKeys), lowerKeys)
}

const VideoWithChat = ({ component }) => {
  const [messages, setMessages] = useState([])

  const [lastCalculatedElapsedSecond, setLastCalculatedElapsedSecond] = useState(0)
  const [elapsedSecondsMap, setElapsedSecondsMap] = useState(new Map())
  const { track } = useTracking()

  useEffect(() => {
    if (!component.chatMessages?.url) return
    ;(async () => {
      try {
        const csv = await fetch(component.chatMessages.url)
        const text = await csv.text()
        setElapsedSecondsMap(csvToObjects(text))
      } catch (error) {
        console.error(error)
      }
    })()
  }, [component.chatMessages?.url])

  useEffect(() => {
    track("Webinar Watched", {
      webinarId: component?.trackingId,
      type: "replay",
    })
  }, [])

  return (
    <VideoWithChatWrapper
      recordedChatMessage={
        <RecordedChatMessage messages={messages} webinarId={component?.trackingId} />
      }
    >
      <ContentfulVideo
        key={component.webinarVideo.id}
        component={component.webinarVideo}
        onTimeCallBack={(event) => {
          const position = Math.floor(event.position)
          if (lastCalculatedElapsedSecond === position || lastCalculatedElapsedSecond > position)
            return
          setLastCalculatedElapsedSecond(position)
          const newMessages = findLowerKeysAndExtract(elapsedSecondsMap, position)

          if (newMessages.length !== messages.length) setMessages(newMessages)
        }}
      />
    </VideoWithChatWrapper>
  )
}

export default VideoWithChat
